import {
  Actions,
  DashboardActionTypes,
} from '../../_actions/settings/dashboards.action';
import { Field } from '../../_models/field.model';
import { Operations, OpTypes } from '../../_models/general.model';
import { Summary } from '../../_models/report.models';
import {
  AppReport,
  DatabaseReports,
  SummaryField,
} from '../../_models/settings.models';

/**
 * State interface
 */
export interface DashboardsState {
  dashboards: {
    [key: string]: Array<any>;
  };
  summaryFields: {
    [key: string]: Array<Field>;
  };
  databaseReports: {
    [key: string]: Array<DatabaseReports>;
  };
  summaryReports: {
    [key: string]: Array<Summary>;
  };
  summaryFieldValue: {
    [appId: string]: {
      [summaryFieldId: string]: SummaryField;
    };
  };
  reports: {
    [appId: string]: {
      map: Array<AppReport>;
      gantt: Array<AppReport>;
      calendar: Array<AppReport>;
    };
  };
  dashboardDetails: {
    [applicationId: string]: {
      [dashboardId: string]: any;
    };
  };
  error: {} | null;
  operations: Operations;
  dashboardsLoading:{[key:string]:boolean};
  databaseReportsLoading:{[key:string]:boolean};
  summaryReportsLoading:{[key:string]:boolean};
  summaryFieldsLoading:{[key:string]:boolean};
  reportsLoading:{[key:string]:boolean};
}

/**
 * Initials State
 */
export const initialState: DashboardsState = {
  dashboards: {},
  dashboardDetails: {},
  summaryFields: {},
  databaseReports: {},
  summaryReports: {},
  summaryFieldValue: {},
  reports: {},
  error: null,
  operations: {},
  dashboardsLoading:{},
  databaseReportsLoading:{},
  summaryReportsLoading:{},
  summaryFieldsLoading:{},
  reportsLoading:{},
};

/**
 * Reducer
 */
export const DashboardsReducers = (
  state = initialState,
  action: Actions
): DashboardsState => {
  switch (action.type) {
    case DashboardActionTypes.LOAD_REPORTS_BY_TYPE:
      return {
        ...state,
        reportsLoading:{...state.reportsLoading,[action.appId]:true, [`error-${action.appId}`]:false}
      }
    case DashboardActionTypes.LOAD_SUMMARY_FIELDS:
      return {
        ...state,
        summaryFieldsLoading:{...state.summaryFieldsLoading,[action.appId]:true, [`error-${action.appId}`]:false}
      }
    case DashboardActionTypes.LOAD_DATABASE_REPORTS:
      return {
        ...state,
        databaseReportsLoading:{...state.databaseReportsLoading, [action.appId]:true, [`error-${action.appId}`]:false}
      }
    case DashboardActionTypes.LOAD_SUMMARY_REPORTS:
      return {
        ...state,
        summaryReportsLoading:{...state.summaryReportsLoading, [action.appId]:true, [`error-${action.appId}`]:false}
      }
    case DashboardActionTypes.COPY_DASHBOARD_SUCCESS:
      return {
        ...state,
        dashboards: {
          ...state.dashboards,
          [action.payload.ApplicationKey]: [
            ...state.dashboards[action.payload.ApplicationKey],
            action.payload,
          ],
        },
        operations: {
          ...state.operations,
          [action.refId]: {
            type: OpTypes.CREATE,
            error: null,
          },
        },
      };
    case DashboardActionTypes.COPY_DASHBOARD_FAILED:
    case DashboardActionTypes.UPDATE_DASHBOARD_FAILED:
    case DashboardActionTypes.SAVE_DASHBOARD_FAILED:
      return {
        ...state,
        operations: {
          ...state.operations,
          [action.refId]: {
            type: OpTypes.FAILED,
            error: action.payload,
          },
        },
        error: action.payload,
      };
    case DashboardActionTypes.LOAD_DASHBOARD:
      return {
        ...state,
        dashboards: {},
        dashboardsLoading:{...state.dashboardsLoading, [action.applicationId]:true, [`error-${action.applicationId}`]:false}
      };
    case DashboardActionTypes.LOAD_DASHBOARDS_SUCCESS:
      return {
        ...state,
        dashboards: {
          ...state.dashboards,
          [action.applicationId]: action.payload,
        },
        dashboardsLoading:{...state.dashboardsLoading, [action.applicationId]:false, [`error-${action.applicationId}`]:false}
      };
    case DashboardActionTypes.LOAD_DASHBOARD_SUCCESS:
      return {
        ...state,
        dashboardDetails: {
          ...state.dashboardDetails,
          [action.applicationId]: {
            ...state.dashboardDetails[action.applicationId],
            [action.dashboardId]: action.payload,
          },
        },
      };
    case DashboardActionTypes.LOAD_SUMMARY_FIELDS_SUCCESS:
      return {
        ...state,
        summaryFields: {
          ...state.summaryFields,
          [action.appId]: action.payload,
        },
        summaryFieldsLoading:{...state.summaryFieldsLoading, [action.appId]:false, [`error-${action.appId}`]:false}
      };
    case DashboardActionTypes.LOAD_DATABASE_REPORTS_SUCCESS:
      return {
        ...state,
        databaseReports: {
          ...state.databaseReports,
          [action.appId]: action.payload,
        },
        databaseReportsLoading:{...state.databaseReportsLoading, [action.appId]:false, [`error-${action.appId}`]:false }
      };
    case DashboardActionTypes.LOAD_SUMMARY_REPORTS_SUCCESS:
      return {
        ...state,
        summaryReports: {
          ...state.summaryReports,
          [action.appId]: action.payload,
        },
        summaryReportsLoading:{...state.summaryReportsLoading, [action.appId]:false, [`error-${action.appId}`]:false},
      };
    case DashboardActionTypes.LOAD_REPORTS_BY_TYPE_SUCCESS:
      return {
        ...state,
        reports: {
          ...state.reports,
          [action.appId]: {
            ...state.reports[action.appId],
            [action.reportType]: action.payload,
          },
        },
        reportsLoading:{...state.reportsLoading, [action.appId]:false, [`error-${action.appId}`]:false}
      };
    case DashboardActionTypes.SAVE_DASHBOARD_SUCCESS:
      const dashboads = [...(state.dashboards[Number(action.appId)] || [])];
      dashboads.push(action.payload);
      dashboads.sort((a, b) => a.Name.localeCompare(b.Name));
      return {
        ...state,
        dashboards: {
          ...state.dashboards,
          [action.appId]: dashboads,
        },
      };
    case DashboardActionTypes.DELETE_DASHBOARD_SUCCESS:
      const index = state.dashboards[action.appId].findIndex(
        (item: any) => item.Key == action.dashboardId
      );

      return {
        ...state,
        dashboards: {
          ...state.dashboards,
          [action.appId]: [
            ...state.dashboards[action.appId].slice(0, index),
            ...state.dashboards[action.appId].slice(index + 1),
          ],
        },
      };
    case DashboardActionTypes.LOAD_SUMMARY_FIELD_VALUE_SUCCESS:
      return {
        ...state,
        summaryFieldValue: {
          ...state.summaryFieldValue,
          [action.appId]: {
            ...state.summaryFieldValue[action.appId],
            [action.summaryFieldId]: action.payload,
          },
        },
      };
    // case DashboardActionTypes.LOAD_REPORTS_BY_TYPE_FAILED:
    case DashboardActionTypes.LOAD_SUMMARY_FIELD_VALUE_FAILED:
    // case DashboardActionTypes.LOAD_SUMMARY_FIELDS_FAILED:
    // case DashboardActionTypes.LOAD_DASHBOARD_FAILED:
    // case DashboardActionTypes.LOAD_DATABASE_REPORTS_FAILED:
    // case DashboardActionTypes.LOAD_SUMMARY_REPORTS_FAILED:
    
    case DashboardActionTypes.DELETE_DASHBOARD_FAILED:
    return {
      ...state,
      dashboards: {},
      error: action.payload,
    };
    case DashboardActionTypes.LOAD_DATABASE_REPORTS_FAILED:
      return{
        ...state,
        dashboards:{},
        error: action.payload,
        databaseReportsLoading:{...state.databaseReportsLoading, [action.appId]:false, [`error-${action.appId}`]:true}
      }
    case DashboardActionTypes.LOAD_SUMMARY_REPORTS_FAILED:
      return{
        ...state,
        dashboards: {},
        error: action.payload,
        summaryReportsLoading:{...state.summaryReportsLoading, [action.appId]:false, [`error-${action.appId}`]:true},
      }
    case DashboardActionTypes.LOAD_DASHBOARDS_FAILED:
    return{
      ...state,
      dashboards:{},
      error: action.payload,
      dashboardsLoading:{...state.dashboardsLoading, [action.applicationId]:false, [`error-${action.applicationId}`]:true}
    }
    case DashboardActionTypes.LOAD_SUMMARY_FIELDS_FAILED:
      return {
        ...state,
        dashboards: {},
        error: action.payload,
        summaryFieldsLoading:{...state.summaryFieldsLoading,[action.appId]:false, [`error-${action.appId}`]:true},
      }
    case DashboardActionTypes.LOAD_REPORTS_BY_TYPE_FAILED:
      return {
        ...state,
        dashboards: {},
        error: action.payload,
        reportsLoading:{...state.reportsLoading, [action.appId]:false, [`error-${action.appId}`]:true}
      }
    case DashboardActionTypes.RESET_DASHBOARD_DETAILS:
      return {
        ...state,
        dashboardDetails: {}
      };
    case DashboardActionTypes.RESET:
      return initialState;
    default:
      return state;
  }
};
